import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';

/**
 * Reusable modal for handling API overload/retry scenarios
 * Shows retry progress and allows manual retry without losing form data
 */
const APIRetryModal = ({ 
  isOpen, 
  attemptNumber, 
  maxAttempts, 
  isRetrying,
  errorMessage,
  onManualRetry,
  onCancel,
  serviceName = "Service"
}) => {
  if (!isOpen) return null;

  const isAutoRetrying = isRetrying && attemptNumber < maxAttempts;
  const autoRetriesFailed = attemptNumber >= maxAttempts && !isRetrying;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <Card className="w-full max-w-full sm:max-w-md bg-white">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-base sm:text-lg md:text-xl font-bold text-slate-900 flex items-center break-words">
            {isAutoRetrying ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-blue-600 mr-2 sm:mr-3 flex-shrink-0"></div>
                <span className="break-words">Retrying {serviceName}...</span>
              </>
            ) : autoRetriesFailed ? (
              <>
                <span className="mr-2">⚠️</span> Retry Needed
              </>
            ) : (
              <>
                <span className="mr-2">⏳</span> Processing...
              </>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4 p-4 sm:p-6 pt-0">
          {/* Status Message */}
          <div className="p-3 sm:p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            {isAutoRetrying ? (
              <div>
                <p className="text-xs sm:text-sm font-semibold text-yellow-800">
                  API Temporarily Overloaded
                </p>
                <p className="text-xs sm:text-sm text-yellow-700 mt-2 break-words">
                  The Claude AI service is experiencing high demand. We're automatically retrying your request...
                </p>
                <div className="mt-2 sm:mt-3">
                  <div className="flex justify-between text-[10px] sm:text-xs text-yellow-700 mb-1">
                    <span>Attempt {attemptNumber} of {maxAttempts}</span>
                    <span>{Math.round((attemptNumber / maxAttempts) * 100)}%</span>
                  </div>
                  <div className="w-full bg-yellow-200 rounded-full h-1.5 sm:h-2">
                    <div 
                      className="bg-yellow-600 h-1.5 sm:h-2 rounded-full transition-all duration-500"
                      style={{ width: `${(attemptNumber / maxAttempts) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            ) : autoRetriesFailed ? (
              <div>
                <p className="text-xs sm:text-sm font-semibold text-yellow-800">
                  Service Still Unavailable
                </p>
                <p className="text-xs sm:text-sm text-yellow-700 mt-2 break-words">
                  We tried {maxAttempts} times but the AI service is still overloaded. This is temporary and usually resolves in a few minutes.
                </p>
                <p className="text-xs sm:text-sm text-yellow-700 mt-2 break-words">
                  <strong>Good news:</strong> Your form data is saved! You can try again now or wait a few minutes.
                </p>
              </div>
            ) : (
              <div>
                <p className="text-xs sm:text-sm text-yellow-700 break-words">
                  {errorMessage || "Processing your request..."}
                </p>
              </div>
            )}
          </div>

          {/* Error Details (if available) */}
          {errorMessage && autoRetriesFailed && (
            <div className="text-[10px] sm:text-xs text-slate-500 p-2 sm:p-3 bg-slate-50 rounded border border-slate-200">
              <strong>Technical Details:</strong>
              <p className="mt-1 font-mono break-all">{errorMessage}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            {autoRetriesFailed && (
              <>
                <Button
                  onClick={onManualRetry}
                  className="w-full sm:flex-1 bg-blue-600 hover:bg-blue-700 text-sm sm:text-base py-2.5 sm:py-3"
                  disabled={isRetrying}
                >
                  {isRetrying ? 'Retrying...' : '🔄 Try Again Now'}
                </Button>
                <Button
                  onClick={onCancel}
                  variant="outline"
                  className="w-full sm:flex-1 text-sm sm:text-base py-2.5 sm:py-3"
                  disabled={isRetrying}
                >
                  Cancel
                </Button>
              </>
            )}
            {isAutoRetrying && (
              <div className="flex-1 text-center text-xs sm:text-sm text-slate-600 py-2">
                Automatic retry in progress...
              </div>
            )}
          </div>

          {/* Info Footer */}
          <div className="text-[10px] sm:text-xs text-slate-500 text-center border-t border-slate-200 pt-2 sm:pt-3 break-words">
            {autoRetriesFailed ? (
              <div>
                💡 <strong>Tip:</strong> Peak hours (9 AM - 5 PM US time) may have longer wait times. 
                Off-peak hours typically have instant responses.
              </div>
            ) : (
              <div>
                This is a temporary issue with the AI provider's servers. 
                Your data is safe and no charges have been made.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default APIRetryModal;
